'use strict';

const ELLIPSIS = '\u2026';

export const removeUrlPath = (url) => {
  return url.replace(/^(?:https?:\/\/)?(?:www\.)?/, '');
};

export const getPathname = (url) => {
  try {
    const urlWithProtocol = /^https?:\/\//.test(url) ? url : `https://${url}`;
    return (new URL(urlWithProtocol)).pathname;
  } catch (error) {
    return url;
  }
};

export const getHostname = (url) => {
  try {
    const urlWithProtocol = /^https?:\/\//.test(url) ? url : `https://${url}`;
    const { hostname } = new URL(urlWithProtocol);
    return hostname;
  } catch (err) {
    return url;
  }
};

export const getSubFolder = (url) => {
  try {
    const urlWithProtocol = /^https?:\/\//.test(url) ? url : `https://${url}`;
    const { pathname } = new URL(urlWithProtocol);
    const leadingTrailingSlashesRegex = /^\/|\/$/g;
    return pathname.replace(leadingTrailingSlashesRegex, '');
  } catch (err) {
    return url;
  }
};

export const getReviewedUrl = (url) => {
  try {
    const { hostname, pathname, search } = new URL(url);
    let result = hostname;
    if (pathname !== '/') {
      result += pathname;
    }
    if (search) {
      result += search;
    }
    return result;
  } catch (error) {
    return url;
  }
};

export const isHomepage = (url) => {
  try {
    const urlWithProtocol = /^https?:\/\//.test(url) ? url : `https://${url}`;
    const { pathname, search } = new URL(urlWithProtocol);
    return pathname === '/' && search === '';
  } catch (err) {
    return true;
  }
};

export const isValidReviewUrl = (url, { homepageOnly = false } = {}) => {
  const urlWithProtocol = /^https?:\/\//.test(url) ? url : `https://${url}`;
  try {
    const parsed = new URL(urlWithProtocol);
    if (!parsed.hostname.includes('.') || parsed.hostname.startsWith('.') || parsed.hostname.endsWith('.')) {
      return false;
    }
    if (homepageOnly) {
      return parsed.pathname === '/' && !parsed.search && !parsed.hash;
    } else {
      return true;
    }
  } catch (e) {
    return false;
  }
};

export const getLocation = (originalUrl, params = {}) => {
  const dummyBase = 'http://x';
  const location = new URL(originalUrl, dummyBase);
  Object.keys(params).forEach(key => {
    location.searchParams.set(key, params[key]);
  });
  return location.toString().slice(dummyBase.length);
};

// https://github.com/GoogleChrome/lighthouse/blob/45f8d01951ea1933aa48089d316262924a3ee310/lighthouse-core/report/html/renderer/util.js#L268
export const getURLDisplayName = (url, options = {}) => {
  let parsedUrl;
  try {
    parsedUrl = new URL(url);
  } catch (e) {
    return url;
  }
  const numPathParts = options.numPathParts !== undefined ? options.numPathParts : 2;
  const preserveQuery = options.preserveQuery !== undefined ? options.preserveQuery : true;
  const preserveHost = options.preserveHost || false;

  let name;

  if (parsedUrl.protocol === 'about:' || parsedUrl.protocol === 'data:') {
    // Handle 'about:*' and 'data:*' URLs specially since they have no path.
    name = parsedUrl.href;
  } else {
    name = parsedUrl.pathname;
    const parts = name.split('/').filter(part => part.length);
    if (numPathParts && parts.length > numPathParts) {
      name = ELLIPSIS + parts.slice(-1 * numPathParts).join('/');
    }

    if (preserveHost) {
      name = `${parsedUrl.host}/${name.replace(/^\//, '')}`;
    }
    if (preserveQuery) {
      name = `${name}${parsedUrl.search}`;
    }
  }

  const MAX_LENGTH = 64;
  // Always elide hexadecimal hash
  name = name.replace(/([a-f0-9]{7})[a-f0-9]{13}[a-f0-9]*/g, `$1${ELLIPSIS}`);
  // Also elide other hash-like mixed-case strings
  name = name.replace(/([a-zA-Z0-9-_]{9})(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])[a-zA-Z0-9-_]{10,}/g,
    `$1${ELLIPSIS}`);
  // Also elide long number sequences
  name = name.replace(/(\d{3})\d{6,}/g, `$1${ELLIPSIS}`);
  // Merge any adjacent ellipses
  name = name.replace(/\u2026+/g, ELLIPSIS);

  // Elide query params first
  if (name.length > MAX_LENGTH && name.includes('?')) {
    // Try to leave the first query parameter intact
    name = name.replace(/\?([^=]*)(=)?.*/, `?$1$2${ELLIPSIS}`);

    // Remove it all if it's still too long
    if (name.length > MAX_LENGTH) {
      name = name.replace(/\?.*/, `?${ELLIPSIS}`);
    }
  }

  // Elide too long names next
  if (name.length > MAX_LENGTH) {
    const dotIndex = name.lastIndexOf('.');
    if (dotIndex >= 0) {
      name = name.slice(0, MAX_LENGTH - 1 - (name.length - dotIndex)) +
        // Show file extension
        `${ELLIPSIS}${name.slice(dotIndex)}`;
    } else {
      name = name.slice(0, MAX_LENGTH - 1) + ELLIPSIS;
    }
  }

  return name;
};
